import type { User } from '../types/auth';

export type RoleRouteMatrixVersion = 'v1.0';

export const ROLE_ROUTE_MATRIX_VERSION: RoleRouteMatrixVersion = 'v1.0';

export type NavIconKey =
  | 'dashboard'
  | 'campaigns'
  | 'screens'
  | 'bookings'
  | 'logs'
  | 'monitor'
  | 'analytics'
  | 'settings'
  | 'discover'
  | 'payouts'
  | 'machines'
  | 'verifications'
  | 'visibility'
  | 'media'
  | 'players'
  | 'releases'
  | 'install'
  | 'more';

export interface NavigationItem {
  text: string;
  path: string;
  iconKey: NavIconKey;
  /** Key under locales/<lng>/common.json; `text` is the English fallback. */
  labelKey?: string;
}

/** Translation keys for the labels the locale files know; anything else shows its English text. */
const LABEL_KEYS: Record<string, string> = {
  'Dashboard': 'nav.dashboard',
  'My Screens': 'nav.myScreens',
  'Screens': 'nav.screens',
  'All Screens': 'nav.screens',
  'Booking Requests': 'nav.bookingRequests',
  'Bookings': 'nav.bookings',
  'My Bookings': 'nav.bookings',
  'All Bookings': 'nav.bookings',
  'Money': 'nav.money',
  'Logs': 'nav.logs',
  'Camera Log': 'nav.cameraLog',
  'Analytics': 'nav.analytics',
  'Screen Analytics': 'nav.analytics',
  'Campaign Analytics': 'nav.analytics',
  'Platform Analytics': 'nav.analytics',
  'Campaigns': 'nav.campaigns',
  'All Campaigns': 'nav.campaigns',
  'Payouts': 'nav.payouts',
  'Notifications': 'nav.notifications',
  'Settings': 'nav.settings',
  'Player Install Guide': 'nav.installGuide',
};

function withLabelKeys(items: NavigationItem[]): NavigationItem[] {
  return items.map((item) => (LABEL_KEYS[item.text] ? { ...item, labelKey: LABEL_KEYS[item.text] } : item));
}

interface RoleContext {
  role?: User['role'];
  isPrivate?: boolean;
  accountType?: User['accountType'];
}

export function getSidebarNavigation(context: RoleContext): NavigationItem[] {
  return withLabelKeys(sidebarNavigation(context));
}

function sidebarNavigation(context: RoleContext): NavigationItem[] {
  const { role, isPrivate = false } = context;

  if (role === 'ScreenOwner') {
    const items: NavigationItem[] = [
      { text: 'Dashboard', iconKey: 'dashboard', path: '/dashboard' },
      { text: 'My Screens', iconKey: 'screens', path: '/screens' },
    ];

    if (!isPrivate) {
      items.push(
        { text: 'Booking Requests', iconKey: 'bookings', path: '/bookings' },
        { text: 'My videos', iconKey: 'media', path: '/library' },
        { text: 'Money', iconKey: 'payouts', path: '/money' },
      );
    } else {
      items.push({ text: 'Screen Analytics', iconKey: 'analytics', path: '/analytics' });
    }

    items.push(
      { text: 'Logs', iconKey: 'logs', path: '/logs' },
      { text: 'Player Install Guide', iconKey: 'install', path: '/help/install-player' },
      { text: 'Settings', iconKey: 'settings', path: '/profile' },
    );

    return items;
  }

  if (role === 'Advertiser') {
    // Named after what the advertiser does; the report pages hang off Results.
    return [
      { text: 'Home', iconKey: 'dashboard', path: '/dashboard' },
      { text: 'Campaigns', iconKey: 'campaigns', path: '/campaigns' },
      { text: 'Monitoring room', iconKey: 'monitor', path: '/monitor' },
      { text: 'Find screens', iconKey: 'discover', path: '/screens/discover' },
      { text: 'Bookings', iconKey: 'bookings', path: '/bookings' },
      { text: 'Creatives', iconKey: 'media', path: '/library' },
      { text: 'Results', iconKey: 'analytics', path: '/analytics' },
      { text: 'Wallet and invoices', iconKey: 'payouts', path: '/wallet' },
      { text: 'Settings', iconKey: 'settings', path: '/profile' },
    ];
  }

  return [
    { text: 'Dashboard', iconKey: 'dashboard', path: '/dashboard' },
    { text: 'All Campaigns', iconKey: 'campaigns', path: '/campaigns' },
    { text: 'All Screens', iconKey: 'screens', path: '/screens' },
    { text: 'All Bookings', iconKey: 'bookings', path: '/bookings' },
    { text: 'Player Fleet', iconKey: 'players', path: '/admin/player-fleet' },
    { text: 'Player Releases', iconKey: 'releases', path: '/admin/player-releases' },
    { text: 'Live View Limits', iconKey: 'monitor', path: '/admin/live-view' },
    { text: 'Payouts', iconKey: 'payouts', path: '/admin/payouts' },
    { text: 'Machines', iconKey: 'machines', path: '/admin/machines' },
    { text: 'Verifications', iconKey: 'verifications', path: '/admin/verifications' },
    { text: 'Creative Review', iconKey: 'media', path: '/admin/creatives/review' },
    { text: 'Visibility Requests', iconKey: 'visibility', path: '/admin/visibility-requests' },
    { text: 'Vision Requests', iconKey: 'visibility', path: '/admin/vision-requests' },
    { text: 'Camera Fleet', iconKey: 'visibility', path: '/admin/camera-fleet' },
    { text: 'Camera Log', iconKey: 'logs', path: '/logs?tab=camera' },
    { text: 'Leads', iconKey: 'bookings', path: '/admin/leads' },
    { text: 'Audit Log', iconKey: 'logs', path: '/admin/audit' },
    { text: 'Platform Analytics', iconKey: 'analytics', path: '/analytics' },
    { text: 'Player Install Guide', iconKey: 'install', path: '/help/install-player' },
    { text: 'Settings', iconKey: 'settings', path: '/profile' },
  ];
}

export function getMobileNavigation(context: RoleContext): NavigationItem[] {
  return withLabelKeys(mobileNavigation(context));
}

function mobileNavigation(context: RoleContext): NavigationItem[] {
  const { role, isPrivate = false } = context;

  if (role === 'Advertiser') {
    // Bookings is where advertisers pay, so it has to be on the bar; the rest
    // (Creatives, Results, Settings) sits behind More, which opens the drawer.
    return [
      { text: 'Home', iconKey: 'dashboard', path: '/dashboard' },
      { text: 'Campaigns', iconKey: 'campaigns', path: '/campaigns' },
      { text: 'Screens', iconKey: 'discover', path: '/screens/discover' },
      { text: 'Bookings', iconKey: 'bookings', path: '/bookings' },
      { text: 'More', iconKey: 'more', path: '#more' },
    ];
  }

  if (role === 'ScreenOwner') {
    const items: NavigationItem[] = [
      { text: 'Dashboard', iconKey: 'dashboard', path: '/dashboard' },
      { text: 'Screens', iconKey: 'screens', path: '/screens' },
    ];

    if (isPrivate) {
      items.push({ text: 'Analytics', iconKey: 'analytics', path: '/analytics' });
    } else {
      items.push(
        { text: 'Bookings', iconKey: 'bookings', path: '/bookings' },
        { text: 'Money', iconKey: 'payouts', path: '/money' },
      );
    }

    return items;
  }

  return [
    { text: 'Dashboard', iconKey: 'dashboard', path: '/dashboard' },
    { text: 'Screens', iconKey: 'screens', path: '/screens' },
    { text: 'Bookings', iconKey: 'bookings', path: '/bookings' },
    { text: 'Analytics', iconKey: 'analytics', path: '/analytics' },
  ];
}

export interface AccessContext {
  role?: User['role'];
  isPrivate?: boolean;
}

export function canAccessBookings(context: AccessContext): boolean {
  return !(context.role === 'ScreenOwner' && context.isPrivate);
}

export function canAccessPayouts(context: AccessContext): boolean {
  return !(context.role === 'ScreenOwner' && context.isPrivate);
}

export function canAccessAdmin(context: AccessContext): boolean {
  return context.role === 'Admin';
}

/** Vision/camera surfaces: screen owners (their own cameras) and admins (all). */
export function canAccessVision(context: AccessContext): boolean {
  return context.role === 'ScreenOwner' || context.role === 'Admin';
}

/** Player install guide and device setup help: screen owners and admins. */
export function canAccessPlayerSetup(context: AccessContext): boolean {
  return context.role === 'ScreenOwner' || context.role === 'Admin';
}
